import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Pencil } from "lucide-react";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";

interface Event {
  _id: string;
  name: string;
}

interface Tour {
  _id: string;
  name: string;
  events: Event[];
}

export function TourView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tour, setTour] = useState<Tour | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [eventName, setEventName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    async function fetchTour() {
      if (!id) return;

      try {
        const response = await fetch(`/api/tours/${id}`);
        if (response.ok) {
          const data = await response.json();
          setTour(data.tour);
          // Update breadcrumbs with tour name
          if (data.tour?.name) {
            setBreadcrumbs([
              { label: "Tours", path: "/tours" },
              { label: data.tour.name, path: `/tours/${id}` },
            ]);
          }
        } else {
          console.error("Failed to fetch tour");
        }
      } catch (error) {
        console.error("Failed to fetch tour:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchTour();

    // Cleanup: clear breadcrumbs when component unmounts
    return () => {
      clearBreadcrumbs();
    };
  }, [id]);

  async function handleCreateEvent() {
    if (!eventName.trim() || !id) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`/api/tours/${id}/events`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name: eventName.trim() }),
      });

      if (response.ok) {
        const data = await response.json();
        // Refresh tour data to include the new event
        const tourResponse = await fetch(`/api/tours/${id}`);
        if (tourResponse.ok) {
          const tourData = await tourResponse.json();
          setTour(tourData.tour);
        }
        setIsDrawerOpen(false);
        setEventName("");
      } else {
        console.error("Failed to create event");
      }
    } catch (error) {
      console.error("Failed to create event:", error);
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  if (!tour) {
    return <div className="p-4">Tour not found</div>;
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold">Events</h1>
        <Button onClick={() => setIsDrawerOpen(true)}>
          <Plus className="h-4 w-4" />
          Add Event
        </Button>
      </div>

      <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <DrawerContent className="max-w-2xl mx-auto">
          <DrawerHeader>
            <DrawerTitle>Create Event</DrawerTitle>
          </DrawerHeader>
          <div className="p-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="event-name">Event Name</Label>
              <Input
                id="event-name"
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && eventName.trim() && !isSubmitting) {
                    handleCreateEvent();
                  }
                }}
                autoFocus
                placeholder="Enter event name"
                disabled={isSubmitting}
              />
            </div>
          </div>
          <DrawerFooter className="flex-row gap-2 justify-center">
            <DrawerClose asChild>
              <Button variant="outline" disabled={isSubmitting}>
                Cancel
              </Button>
            </DrawerClose>
            <Button
              onClick={handleCreateEvent}
              disabled={!eventName.trim() || isSubmitting}
            >
              {isSubmitting ? "Creating..." : "Create"}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead className="w-[100px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tour.events && tour.events.length > 0 ? (
            tour.events.map((event) => (
              <TableRow key={event._id}>
                <TableCell>{event.name}</TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate(`/events/${event._id}/edit`)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={2} className="h-24 text-center">
                No events found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

